// API service for managing plays in DynamoDB
import { DynamoDBClient } from '@aws-sdk/client-dynamodb'
import { 
  DynamoDBDocumentClient, 
  PutCommand, 
  GetCommand, 
  QueryCommand, 
  DeleteCommand,
  UpdateCommand 
} from '@aws-sdk/lib-dynamodb'
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth'
import { nanoid } from 'nanoid'

// Initialize DynamoDB client with credentials from Amplify
let docClient = null

const getDocClient = async () => {
  if (docClient) return docClient
  
  // Check if Identity Pool is configured
  const identityPoolId = import.meta.env.VITE_AWS_IDENTITY_POOL_ID
  if (!identityPoolId) {
    throw new Error('Identity Pool ID is not configured. Please set VITE_AWS_IDENTITY_POOL_ID in your .env file.')
  }
  
  try {
    // First, verify user is authenticated
    try {
      await getCurrentUser()
    } catch (authError) {
      throw new Error('User is not authenticated. Please log in first.')
    }
    
    // Fetch auth session - force refresh to ensure we get Identity Pool credentials
    const session = await fetchAuthSession({ forceRefresh: true })
    const credentials = session.credentials
    
    if (!credentials || !credentials.accessKeyId) {
      throw new Error('No AWS credentials available. Please ensure:\n1. Identity Pool is configured correctly\n2. You are authenticated\n3. Identity Pool is linked to your User Pool\n4. Try signing out and signing back in')
    }
    
    const client = new DynamoDBClient({ 
      region: import.meta.env.VITE_AWS_REGION || 'us-east-2',
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken
      }
    })
    
    docClient = DynamoDBDocumentClient.from(client, {
      marshallOptions: {
        removeUndefinedValues: true
      }
    })
    return docClient
  } catch (error) {
    // Re-throw with more context if it's our custom error
    if (error.message.includes('Identity Pool') || error.message.includes('credentials')) {
      throw error
    }
    
    throw new Error(`Failed to get AWS credentials: ${error.message}. Please check your Identity Pool configuration.`)
  }
}

const TABLE_NAME = import.meta.env.VITE_DYNAMODB_TABLE_NAME || 'sport-plays'

// Get current user ID (Identity Pool identity ID - full format)
// This matches the IAM policy condition: ${cognito-identity.amazonaws.com:sub}
// Note: We still use the full identityId in DynamoDB for IAM policy matching
const getUserId = async () => {
  try {
    // First verify user is authenticated
    await getCurrentUser()
    
    // Get Identity Pool identity ID from session
    const session = await fetchAuthSession({ forceRefresh: true })
    
    if (!session.identityId) {
      throw new Error('Identity Pool identity ID not available. Please ensure:\n1. Identity Pool is properly linked to your User Pool in AWS Console\n2. You have signed out and signed back in after configuring Identity Pool\n3. Identity Pool ID is correctly set in .env file')
    }
    
    return session.identityId
  } catch (error) {
    if (error.message.includes('Identity Pool') || error.message.includes('identity ID')) {
      throw error
    }
    throw new Error('User not authenticated')
  }
}

// Save a play (create or update)
export const savePlay = async (playData, isPublic = false) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  // Use nanoid for shorter play IDs (8 characters, URL-safe)
  // Keep existing playId if provided (for updates), otherwise generate new one
  const playId = playData.id || nanoid(8)
  const now = new Date().toISOString()
  
  const tags = playData.tags || []
  const item = {
    userId,
    playId,
    name: playData.name || 'Untitled Play',
    description: playData.description || '',
    sportType: playData.sportType || 'soccer',
    tags: tags, // Array of tag strings
    // Store first tag for GSI (sparse index - only items with tags will have this)
    // This allows efficient querying by tag using tags-index
    ...(tags.length > 0 && { tag: tags[0] }),
    teams: playData.teams || [],
    props: playData.props || [],
    animations: playData.animations || [],
    fieldSettings: playData.fieldSettings || {},
    isPublic: isPublic ? 'true' : 'false', // Store as string for GSI
    updatedAt: now, // Store as string for GSI sorting
    createdAt: playData.createdAt || now,
    // GIF fields
    gifUrl: playData.gifUrl || null,
    gifStatus: playData.gifStatus || 'pending', // 'pending', 'completed', 'failed'
    gifGeneratedAt: playData.gifGeneratedAt || null
  }
  
  try {
    await client.send(new PutCommand({
      TableName: TABLE_NAME,
      Item: item
    }))
    
    return {
      ...item,
      id: playId,
      isPublic: isPublic
    }
  } catch (error) {
    console.error('Error saving play:', error)
    throw new Error('Failed to save play: ' + error.message)
  }
}

// Load a play by ID
// isUserOwnPlay: 
//   - true: Load as the current user's own play (from "My Plays" tab)
//   - false: Load as a public play (from "Public Plays" tab)
//   - undefined/null: Try user's own first, then fallback to public (for URL-based loading)
export const loadPlay = async (playId, isUserOwnPlay = undefined) => {
  if (isUserOwnPlay === true) {
    // Explicitly load as the current user's own play
    return await loadUserPlay(playId)
  } else if (isUserOwnPlay === false) {
    // Explicitly load as a public play
    return await loadPublicPlay(playId)
  } else {
    // Unknown context (e.g., URL loading) - try user's own first, then public
    try {
      return await loadUserPlay(playId)
    } catch (error) {
      // If user's own play fails, try public play
      return await loadPublicPlay(playId)
    }
  }
}

// Load a play that belongs to the current user
export const loadUserPlay = async (playId) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    const result = await client.send(new GetCommand({
      TableName: TABLE_NAME,
      Key: {
        userId,
        playId
      }
    }))
    
    if (!result.Item) {
      throw new Error('Play not found')
    }
    
    return formatPlayFromDynamo(result.Item)
  } catch (error) {
    console.error('Error loading user\'s play:', error)
    throw new Error('Failed to load play: ' + error.message)
  }
}

// Load a public play by ID
export const loadPublicPlay = async (playId) => {
  const client = await getDocClient()
  
  try {
    // Query public plays index to find the play
    // Note: FilterExpression is applied AFTER Limit, so we don't use Limit to ensure
    // we can find the play even if it's not in the first N items returned
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      IndexName: 'public-plays-index',
      KeyConditionExpression: 'isPublic = :public',
      FilterExpression: 'playId = :playId',
      ExpressionAttributeValues: {
        ':public': 'true',
        ':playId': playId
      }
    }))
    
    if (result.Items && result.Items.length > 0) {
      return formatPlayFromDynamo(result.Items[0])
    }
    
    throw new Error('Play not found')
  } catch (error) {
    console.error('Error loading public play:', error)
    throw new Error('Failed to load play: ' + error.message)
  }
}

// Get all plays for the current user
export const getUserPlays = async () => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      KeyConditionExpression: 'userId = :userId',
      ExpressionAttributeValues: {
        ':userId': userId
      },
      ScanIndexForward: false // Sort by playId descending (newest first)
    }))
    
    return (result.Items || []).map(formatPlayFromDynamo)
  } catch (error) {
    console.error('Error getting user plays:', error)
    throw new Error('Failed to get plays: ' + error.message)
  }
}

// Get all public plays
export const getPublicPlays = async (limit = 50) => {
  const client = await getDocClient()
  
  try {
    const result = await client.send(new QueryCommand({
      TableName: TABLE_NAME,
      IndexName: 'public-plays-index',
      KeyConditionExpression: 'isPublic = :public',
      ExpressionAttributeValues: {
        ':public': 'true'
      },
      ScanIndexForward: false, // Sort by updatedAt descending (newest first)
      Limit: limit
    }))
    
    return (result.Items || []).map(formatPlayFromDynamo)
  } catch (error) {
    console.error('Error getting public plays:', error)
    throw new Error('Failed to get public plays: ' + error.message)
  }
}

// Delete a play
export const deletePlay = async (playId) => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    await client.send(new DeleteCommand({
      TableName: TABLE_NAME,
      Key: {
        userId,
        playId
      }
    }))
    
    return true
  } catch (error) {
    console.error('Error deleting play:', error)
    throw new Error('Failed to delete play: ' + error.message)
  }
}

// Update play GIF URL
export const updatePlayGif = async (playId, gifUrl, status = 'completed') => {
  const userId = await getUserId()
  const client = await getDocClient()
  
  try {
    await client.send(new UpdateCommand({
      TableName: TABLE_NAME,
      Key: {
        userId,
        playId
      },
      UpdateExpression: 'SET gifUrl = :gifUrl, gifStatus = :gifStatus, gifGeneratedAt = :gifGeneratedAt, updatedAt = :updatedAt',
      ExpressionAttributeValues: {
        ':gifUrl': gifUrl,
        ':gifStatus': status,
        ':gifGeneratedAt': new Date().toISOString(),
        ':updatedAt': new Date().toISOString()
      }
    }))
    
    return true
  } catch (error) {
    console.error('Error updating play GIF:', error)
    throw new Error('Failed to update play GIF: ' + error.message)
  }
}

// Format play data from DynamoDB format to app format
const formatPlayFromDynamo = (item) => {
  return {
    id: item.playId,
    name: item.name,
    description: item.description || '',
    sportType: item.sportType || 'soccer',
    tags: item.tags || [], // Array of tag strings
    teams: item.teams || [],
    props: item.props || [],
    animations: item.animations || [],
    fieldSettings: item.fieldSettings || {},
    isPublic: item.isPublic === 'true',
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    userId: item.userId, // Keep for reference
    gifUrl: item.gifUrl || null,
    gifStatus: item.gifStatus || 'pending',
    gifGeneratedAt: item.gifGeneratedAt || null
  }
}

